import React, { useState } from "react";
import { Refrigerator, Package, Phone, WheatOff, MilkOff, UtensilsCrossed } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  const filename = imageUrl.replace("/uploads/", "");
  return `${BACKEND_URL}/api/images/${filename}?v=${Date.now()}`;
};

const ProductCard = ({ product, onAddToCart, isInCart = false, categoryName = "", onClick, cartQuantity = 0, isGastronomia = false, isBusiness = false, isSpecialList = false, isSpecialWithActiveList = false, listQuantity = 0 }) => {
  const [imgError, setImgError] = useState(false);
  
  const getDurationText = () => {
    if (categoryName === "Dolci" || categoryName === "Tramezzini Gourmet") {
      return "Scadenza: non meno di 8 giorni";
    }
    return "Scadenza: non meno di 12 giorni";
  };

  const stock = product.stock || 0;
  const isOutOfStock = stock <= 0 || product.sold_out;
  const remainingStock = stock - cartQuantity;
  const isDolci = categoryName === "Dolci";
  const imageUrl = product.image_url ? getImageUrl(product.image_url) : null;
  const showImage = imageUrl && !imgError;

  return (
    <div 
      onClick={onClick}
      className="bg-zinc-900 rounded-lg border border-zinc-800 hover:border-green-500 transition-all cursor-pointer overflow-hidden" 
      data-testid={`product-card-${product.id}`}
    >
      <div className="flex">
        {/* Product Image */}
        <div className="w-28 h-28 sm:w-36 sm:h-36 flex-shrink-0 bg-zinc-800">
          {showImage ? (
            <img
              src={imageUrl}
              alt={product.name}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
              loading="lazy"
              data-testid={`product-image-${product.id}`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <UtensilsCrossed className="w-8 h-8 text-zinc-700" />
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between min-w-0">
          <div>
            <div className="flex items-start gap-2 mb-1">
              <h3 className="text-base sm:text-lg font-bold text-white flex-1 leading-tight flex items-center gap-1" data-testid="product-name">
                {product.name}
                {isDolci && (
                  <span className="inline-flex items-center gap-0.5">
                    <span className="text-yellow-400" title="Senza Glutine">
                      <WheatOff className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-blue-400" title="Senza Lattosio">
                      <MilkOff className="w-3.5 h-3.5" />
                    </span>
                  </span>
                )}
              </h3>
              {isOutOfStock ? (
                <span className="bg-red-500 text-white text-xs font-semibold px-2 py-0.5 rounded flex-shrink-0"
                  data-testid="sold-out-badge">
                  Esaurito
                </span>
              ) : stock <= 5 && (
                <span className="bg-yellow-500 text-black text-xs font-semibold px-2 py-0.5 rounded flex-shrink-0"
                  data-testid="low-stock-badge">
                  Ultimi {stock}!
                </span>
              )}
            </div>
            
            <p className="text-zinc-400 text-xs sm:text-sm mb-2 line-clamp-2" data-testid="product-description">
              {product.short_description}
            </p>
            
            <div className="flex items-center gap-2 flex-wrap">
              <span className="bg-green-500/10 text-green-500 text-xs font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                data-testid="fresh-badge">
                <Refrigerator className="w-3 h-3" />
                Fresco
              </span>
              {product.weight && (
                <span className="bg-yellow-500/10 text-yellow-500 text-xs font-semibold px-1.5 py-0.5 rounded">
                  {product.weight}
                </span>
              )}
              <span className="text-xs text-zinc-500 hidden sm:inline" data-testid="product-duration">
                {getDurationText()}
              </span>
              {!isOutOfStock && (
                <span className="text-xs text-zinc-500 inline-flex items-center gap-1" data-testid="stock-info">
                  <Package className="w-3 h-3" />
                  {stock}
                </span>
              )}
            </div>
          </div>

          {/* Price + Button row */}
          <div className="flex items-end justify-between mt-2">
            <div>
              <span className="text-xl sm:text-2xl font-bold text-white" data-testid="product-price">
                €{product.price.toFixed(2)}
              </span>
              <p className="text-xs text-zinc-500">{isBusiness ? "IVA esclusa" : "IVA inclusa"}</p>
            </div>
            
            {isGastronomia ? (
              <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg px-3 py-1.5">
                <p className="text-yellow-500 text-xs sm:text-sm font-semibold flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5" />
                  Su ordinazione
                </p>
              </div>
            ) : isSpecialWithActiveList ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToCart(product);
                }}
                className="px-4 sm:px-6 py-2 rounded-lg font-semibold transition-all whitespace-nowrap text-sm bg-yellow-500/20 text-yellow-500 border border-yellow-500/50 hover:bg-yellow-500/30"
                data-testid="add-to-cart-button"
              >
                Vai alla Lista
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToCart(product);
                }}
                disabled={isOutOfStock || remainingStock <= 0}
                className={`px-4 sm:px-6 py-2 rounded-lg font-semibold transition-all whitespace-nowrap text-sm ${
                  isOutOfStock || remainingStock <= 0
                    ? "bg-zinc-800 text-zinc-600 cursor-not-allowed"
                    : isSpecialList
                      ? "bg-yellow-500 text-black hover:bg-yellow-600"
                      : "bg-green-500 text-black hover:bg-green-600"
                }`}
                data-testid="add-to-cart-button"
              >
                {isOutOfStock ? "Esaurito" : remainingStock <= 0 ? "Max" : isSpecialList ? (listQuantity > 0 ? `Lista (${listQuantity})` : "In Lista") : "Aggiungi"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
